<!--
  CreatePeriodModal.vue

  Purpose: Wizard for creating new payroll periods

  Architecture:
  - Two-step wizard using WizardModal
  - Step 1: User+Franchise selection using ButtonsList component
    - Shows users from selected franchises in filter service
    - One entry per user+franchise combination
    - Display format: "User Name - Franchise Name (Último: DD/MM/YYYY)" or "(Sem período anterior)"
    - Last period date fetched via bulk API for efficiency
  - Step 2: Date configuration (start auto-calculated if not first period)
  - After creation, opens PayrollModal for conciliation wizard

  Flow:
  1. User clicks "Novo Período" in PayrollPage
  2. Wizard opens, Step 1 shows list of user+franchise combinations with last period dates
  3. User selects a user+franchise combination
  4. Step 2 shows date configuration
  5. Start datetime auto-filled from last period (or manual if first)
  6. User sets end datetime
  7. User clicks "Criar Período"
  8. Period created via API
  9. Wizard closes
  10. PayrollModal opens for conciliation
-->
<template>
  <WizardModal
    v-if="isVisible"
    :show-modal="isVisible"
    :modal-title="t('Criar Período de Folha de Pagamento')"
    size="large"
    :steps="wizardSteps"
    :linear="true"
    :allow-step-click="false"
    :custom-buttons="true"
    :next-label="getNextLabel"
    :disable-next="isNextDisabled"
    :loading="isLoading"
    @next="handleNext"
    @finish="handleFinish"
    @cancel="handleClose"
    @close="handleClose"
  >
    <!-- Step 1: User+Franchise Selection -->
    <template #step-0>
      <div class="step-content">
        <h3>{{ t('Selecione o Colaborador') }}</h3>
        <p class="step-description">
          {{ t('Escolha o colaborador e franquia para criar um novo período de folha de pagamento.') }}
        </p>

        <!-- Helper Banner -->
        <div class="helper-banner">
          <i class="pi pi-info-circle"></i>
          <div class="helper-content">
            <strong>{{ t('Colaboradores Disponíveis') }}</strong>
            <p>{{ t('Apenas funcionários com regras de pagamento ATIVAS são exibidos nesta lista.') }}</p>
            <ul>
              <li>{{ t('Cada entrada representa uma combinação única: Colaborador + Franquia') }}</li>
              <li>{{ t('Funcionários podem aparecer múltiplas vezes (uma para cada franquia)') }}</li>
              <li>{{ t('Último: DD/MM/YYYY = data final do período mais recente') }}</li>
              <li>{{ t('Sem período anterior = primeiro período para esta combinação') }}</li>
            </ul>
          </div>
        </div>

        <div v-if="isLoadingUsers" class="loading-container">
          <ProgressSpinner style="width: 40px; height: 40px" strokeWidth="4" />
          <span>{{ t('Carregando colaboradores...') }}</span>
        </div>

        <div v-else-if="userFranchiseList.length === 0" class="empty-message">
          <i class="pi pi-info-circle"></i>
          <span>{{ t('Nenhum colaborador com regras de pagamento ativas nas franquias selecionadas') }}</span>
        </div>

        <ButtonsList
          v-else
          :model-value="selectedUserFranchiseKey ?? undefined"
          :itens-select="userFranchiseList"
          elem-id="user-franchise-selection"
          :search-enabled="true"
          :ng-required="true"
          :validation="() => !!selectedUserFranchiseKey"
          @update:model-value="(v: string | number) => { selectedUserFranchiseKey = String(v) }"
        />
      </div>
    </template>

    <!-- Step 2: Date Configuration -->
    <template #step-1>
      <div class="step-content">
        <h3>{{ t('Configurar Período') }}</h3>

        <!-- Selected User+Franchise Summary -->
        <div v-if="selectedUserFranchiseData" class="selected-user-summary">
          <div class="summary-row">
            <i class="pi pi-user"></i>
            <span>{{ t('Colaborador') }}: <strong>{{ selectedUserFranchiseData.userName }}</strong></span>
          </div>
          <div class="summary-row">
            <i class="pi pi-building"></i>
            <span>{{ t('Franquia') }}: <strong>{{ selectedUserFranchiseData.franchiseName }}</strong></span>
          </div>
        </div>

        <!-- Critical Warning Banner -->
        <div class="warning-banner">
          <i class="pi pi-exclamation-triangle"></i>
          <div class="warning-content">
            <strong>{{ t('Atenção às Datas') }}</strong>
            <ul>
              <li>{{ t('Data início: horário será 00:00:00 (início do dia)') }}</li>
              <li>{{ t('Data fim: horário será 23:59:59, ou horário atual se for hoje') }}</li>
              <li>{{ t('Períodos NÃO podem se sobrepor a períodos existentes') }}</li>
              <li>{{ t('Após criar, as datas NÃO poderão ser alteradas') }}</li>
              <li>{{ t('Após criar, o wizard de conciliação abrirá automaticamente') }}</li>
            </ul>
          </div>
        </div>

        <!-- Start Date -->
        <div class="field">
          <label for="startDate">
            {{ t('Data Início') }}
            <span v-if="!isFirstPeriod" class="auto-label">({{ t('Automático') }})</span>
            <span v-else class="required-label">*</span>
          </label>
          <input-date
            elem-id="startDate"
            :placeholder="t('Selecione a data')"
            :disabled="!isFirstPeriod"
            :ng-required="isFirstPeriod"
            :validation="() => !errors.startDate"
            :model-value="startDateStr"
            data-testid="payroll-create-start-date"
            @update:model-value="onStartDateChange"
          />
          <small v-if="errors.startDate" class="p-error">{{ errors.startDate }}</small>
          <small v-if="!isFirstPeriod && formData.startDate" class="info-text">
            {{ t('Início calculado automaticamente a partir do último período') }}
          </small>
          <small class="time-info">
            {{ t('Horário de início será 00:00:00') }}
          </small>
        </div>

        <!-- End Date -->
        <div class="field">
          <label for="endDate">{{ t('Data Fim') }} *</label>
          <input-date
            elem-id="endDate"
            :placeholder="t('Selecione a data')"
            :ng-required="true"
            :validation="() => !errors.endDate"
            :model-value="endDateStr"
            data-testid="payroll-create-end-date"
            @update:model-value="onEndDateChange"
          />
          <small v-if="errors.endDate" class="p-error">{{ errors.endDate }}</small>
          <small class="time-info">
            {{ t('Horário de fim será 23:59:59 (ou horário atual se for hoje)') }}
          </small>
        </div>

        <!-- Duration Card -->
        <div v-if="validationMessage" class="rule-summary">
          <div class="summary-header">
            <i :class="validationMessageType === 'success' ? 'pi pi-check-circle' : 'pi pi-info-circle'"></i>
            <h3>{{ t('Duração do Período') }}</h3>
          </div>
          <div class="summary-content">
            <div class="summary-row">
              <span class="summary-label">{{ t('Total de dias:') }}</span>
              <span class="summary-value amount">{{ validationMessage }}</span>
            </div>
            <div v-if="formData.startDate && formData.endDate" class="summary-row">
              <span class="summary-label">{{ t('Período:') }}</span>
              <span class="summary-value">
                {{ formatDate(formData.startDate) }} → {{ formatDate(formData.endDate) }}
              </span>
            </div>
          </div>
        </div>

        <!-- Duration Validation Warning -->
        <div v-if="durationValidation && durationValidation.type === 'warning'" class="warning-banner" style="margin-top: 1rem;">
          <i class="pi pi-exclamation-triangle"></i>
          <div class="warning-content">
            <strong>{{ t('Atenção') }}</strong>
            <p>{{ durationValidation.message }}</p>
          </div>
        </div>

        <!-- Suggestion Button -->
        <div v-if="getSuggestedEndDate() && !formData.endDate" class="suggestion-container">
          <Button
            :label="getSuggestionLabel()"
            icon="pi pi-lightbulb"
            outlined
            size="small"
            data-testid="payroll-create-apply-suggestion"
            @click="applySuggestion"
          />
        </div>

        <!-- Loading indicator for fetching period data -->
        <div v-if="isLoadingPeriodData" class="loading-indicator">
          <ProgressSpinner style="width: 30px; height: 30px" strokeWidth="4" />
          <span>{{ t('Verificando período anterior...') }}</span>
        </div>
      </div>
    </template>

    <!-- Step 3: Review and Confirm -->
    <template #step-2>
      <div class="step-review">
        <div class="step-header">
          <h2>{{ t('Revise e Confirme') }}</h2>
          <p class="step-description">
            {{ t('Confira todos os dados antes de criar o período.') }}
          </p>
        </div>

        <!-- Warning Banner -->
        <div class="warning-banner">
          <i class="pi pi-exclamation-triangle"></i>
          <div class="warning-content">
            <strong>{{ t('Atenção!') }}</strong>
            <p>{{ t('Após criar, as datas do período NÃO poderão ser alteradas.') }}</p>
          </div>
        </div>

        <!-- Period Summary Card -->
        <div class="rule-summary">
          <div class="summary-header">
            <i class="pi pi-file-text"></i>
            <h3>{{ t('Resumo do Período') }}</h3>
          </div>

          <div class="summary-content">
            <div class="summary-row">
              <span class="summary-label">{{ t('Colaborador:') }}</span>
              <span class="summary-value">{{ selectedUserFranchiseData?.userName }}</span>
            </div>
            <div class="summary-row">
              <span class="summary-label">{{ t('Franquia:') }}</span>
              <span class="summary-value">{{ selectedUserFranchiseData?.franchiseName }}</span>
            </div>
            <div class="summary-row">
              <span class="summary-label">{{ t('Data Início:') }}</span>
              <span class="summary-value">
                {{ formData.startDate ? formatDate(formData.startDate) : '-' }} <small>(00:00:00)</small>
              </span>
            </div>
            <div class="summary-row">
              <span class="summary-label">{{ t('Data Fim:') }}</span>
              <span class="summary-value">
                {{ formData.endDate ? formatDate(formData.endDate) : '-' }} <small>(23:59:59)</small>
              </span>
            </div>
            <div class="summary-row">
              <span class="summary-label">{{ t('Duração:') }}</span>
              <span class="summary-value amount">{{ validationMessage }}</span>
            </div>
          </div>
        </div>

        <!-- Important Notes -->
        <div class="important-notes">
          <div class="notes-header">
            <i class="pi pi-info-circle"></i>
            <h3>{{ t('O Que Acontece Depois') }}</h3>
          </div>
          <ul class="notes-list">
            <li>
              <i class="pi pi-check-circle"></i>
              <span>{{ t('O período será criado e disponível para cálculos de folha') }}</span>
            </li>
            <li>
              <i class="pi pi-forward"></i>
              <span>{{ t('O wizard de conciliação abrirá automaticamente') }}</span>
            </li>
            <li>
              <i class="pi pi-lock"></i>
              <span>{{ t('As datas do período NÃO poderão ser alteradas depois') }}</span>
            </li>
          </ul>
        </div>
      </div>
    </template>
  </WizardModal>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import WizardModal from '@/ui/WizardModal.vue';
import ButtonsList from '@/inputs/ButtonsList.vue';
import { useEmployeePayroll } from '@/composables/useEmployeePayroll';
import { usePaymentRules } from '@/composables/usePaymentRules';
import { useFiltro } from '@/composables/useFiltro';
import { useDateFormatter } from '@/composables/useDateFormatter';
import { permissionsService } from '@/services/permissionsService';
import { eventBus, type EventCallback } from '@/eventBus';
import { log } from '@/utils/DebugLogger';

type Id = number | string;

interface UserFranchiseData {
  userId: Id;
  userName: string;
  franchiseId: Id;
  franchiseName: string;
}

interface UserFranchiseListItem {
  ID: string;
  Name: string;
}

interface PaymentRule {
  user_id: Id;
  franchise_id: Id;
  active: number | string | boolean;
  period_type?: string;
  [key: string]: unknown;
}

interface DurationValidation {
  type: 'warning' | 'success';
  message: string;
}

interface SuggestedEndDate {
  date: Date;
  days: number;
  reason: string;
}

interface FormData {
  startDate: Date | null;
  endDate: Date | null;
}

const { t } = useI18n();
const { getLastPeriodEnd, getLastPeriodsBulk, createPeriodForEmployee } = useEmployeePayroll();
const { listRules } = usePaymentRules();
const { getSelectedFranchisesSelectArray } = useFiltro();
const { fromDateToApiDate, fromApiDateToDate, formatDate } = useDateFormatter();

// =============================================================================
// STATE
// =============================================================================

const isVisible = ref(false);
const isLoading = ref(false);
const isLoadingUsers = ref(false);
const isLoadingPeriodData = ref(false);
const currentStep = ref(0);

// Step 1: User+Franchise selection
// Key format: "userId|franchiseId" to uniquely identify each combination
const selectedUserFranchiseKey = ref<string | null>(null);
const userFranchiseList = ref<UserFranchiseListItem[]>([]);
// Map to store full data for each combination
const userFranchiseMap = ref<Map<string, UserFranchiseData>>(new Map());

// Step 2: Date configuration
const isFirstPeriod = ref(true);

const formData = ref<FormData>({
  startDate: null,
  endDate: null
});

const errors = ref<Record<string, string>>({});
const validationMessage = ref('');
const validationMessageType = ref('info');

// =============================================================================
// COMPUTED
// =============================================================================

// String adapters for input-date (which works with YYYY-MM-DD strings)
const startDateStr = computed((): string => {
  return formData.value?.startDate ? fromDateToApiDate(formData.value.startDate) : '';
});

const endDateStr = computed((): string => {
  return formData.value?.endDate ? fromDateToApiDate(formData.value.endDate) : '';
});

// input-date change handlers (convert YYYY-MM-DD string back to Date)
const onStartDateChange = (value: string): void => {
  formData.value.startDate = value ? fromApiDateToDate(value) : null;
};

const onEndDateChange = (value: string): void => {
  formData.value.endDate = value ? fromApiDateToDate(value) : null;
};

const wizardSteps = computed(() => [
  {
    title: t('Colaborador'),
    description: t('Selecione o colaborador e franquia'),
    validate: () => !!selectedUserFranchiseKey.value
  },
  {
    title: t('Período'),
    description: t('Configure as datas'),
    validate: () => validateStep2()
  },
  {
    title: t('Confirmar'),
    description: t('Revise e confirme'),
    validate: () => true
  }
]);

/**
 * Get the selected user+franchise data from the map
 */
const selectedUserFranchiseData = computed(() => {
  if (!selectedUserFranchiseKey.value) return null;
  return userFranchiseMap.value.get(selectedUserFranchiseKey.value) || null;
});

const isNextDisabled = computed(() => {
  if (currentStep.value === 0) {
    return !selectedUserFranchiseKey.value;
  }
  if (currentStep.value === 1) {
    return !canCreate.value;
  }
  if (currentStep.value === 2) {
    // Review step - always allow finish
    return false;
  }
  return false;
});

const canCreate = computed(() => {
  return (
    selectedUserFranchiseData.value &&
    formData.value?.startDate &&
    formData.value?.endDate &&
    Object.keys(errors.value).length === 0 &&
    !isLoading.value &&
    !isLoadingPeriodData.value
  );
});

/**
 * Duration validation - provides warnings for unusual periods
 */
const durationValidation = computed((): DurationValidation | null => {
  if (!formData.value?.startDate || !formData.value?.endDate) {
    return null;
  }

  const start = new Date(formData.value.startDate);
  const end = new Date(formData.value.endDate);
  const diffMs = end.getTime() - start.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24)) + 1;

  if (diffDays < 7) {
    return {
      type: 'warning',
      message: t('Período muito curto. O típico é 7-30 dias.')
    };
  }

  if (diffDays > 60) {
    return {
      type: 'warning',
      message: t('Período muito longo. O típico é 7-30 dias.')
    };
  }

  return {
    type: 'success',
    message: t('Duração adequada para cálculo de folha.')
  };
});

/**
 * Get suggested end date based on payment rules
 */
const getSuggestedEndDate = (): SuggestedEndDate | null => {
  if (!formData.value?.startDate || !selectedUserFranchiseData.value) {
    return null;
  }

  // Determine period type from payment rules
  const allRules = listRules() as unknown as PaymentRule[] | null;
  if (!allRules || !Array.isArray(allRules)) {
    return null;
  }

  const userData = selectedUserFranchiseData.value;
  const employeeRules = allRules.filter(r =>
    r.user_id === userData.userId &&
    r.franchise_id === userData.franchiseId &&
    (r.active === 1 || r.active === '1' || r.active === true)
  );

  if (employeeRules.length === 0) return null;

  // Determine most common period type
  const periodTypes = employeeRules.map(r => r.period_type);
  const hasWeekly = periodTypes.includes('weekly');
  const hasBiweekly = periodTypes.includes('biweekly');
  const hasMonthly = periodTypes.includes('monthly');

  let suggestedDays = 30; // default
  let reason = 'baseado em regras mensais';

  if (hasWeekly) {
    suggestedDays = 7;
    reason = 'baseado em regras semanais';
  } else if (hasBiweekly) {
    suggestedDays = 14;
    reason = 'baseado em regras quinzenais';
  } else if (hasMonthly) {
    suggestedDays = 30;
    reason = 'baseado em regras mensais';
  }

  const suggestedDate = new Date(formData.value.startDate);
  suggestedDate.setDate(suggestedDate.getDate() + suggestedDays - 1);

  return {
    date: suggestedDate,
    days: suggestedDays,
    reason
  };
};

/**
 * Get suggestion label for button
 */
const getSuggestionLabel = (): string => {
  const suggestion = getSuggestedEndDate();
  if (!suggestion) return '';

  return t('Sugerir {days} dias ({reason})', {
    days: suggestion.days,
    reason: t(suggestion.reason)
  });
};

/**
 * Apply the suggested end date
 */
const applySuggestion = (): void => {
  const suggestion = getSuggestedEndDate();
  if (suggestion) {
    formData.value.endDate = suggestion.date;
    log('CreatePeriodModal', 'applySuggestion', 'Applied suggestion:', suggestion);
  }
};

// =============================================================================
// METHODS
// =============================================================================

/**
 * Get next button label based on current step
 */
const getNextLabel = (step: number): string => {
  if (step === 2) {
    return isLoading.value ? t('Criando...') : t('Criar Período');
  }
  return t('Próximo');
};

/**
 * Validate step 2 form
 */
const validateStep2 = (): boolean => {
  return !!(
    selectedUserFranchiseData.value &&
    formData.value?.startDate &&
    formData.value?.endDate &&
    Object.keys(errors.value).length === 0
  );
};

/**
 * Handle next button click
 */
const handleNext = async (): Promise<void> => {
  log('CreatePeriodModal', 'handleNext', 'Next clicked', { currentStep: currentStep.value });

  if (currentStep.value === 0) {
    if (selectedUserFranchiseKey.value) {
      currentStep.value = 1;
      // Fetch last period for the selected user+franchise to auto-calculate start date
      await fetchLastPeriod();
    }
  } else if (currentStep.value === 1) {
    // Move to review step
    currentStep.value = 2;
  }
};

/**
 * Handle finish (create period)
 * Sends date-only values to API - backend sets appropriate times
 */
const handleFinish = async (): Promise<void> => {
  log('CreatePeriodModal', 'handleFinish', 'Finish clicked');

  if (!validateForm()) {
    return;
  }

  const userData = selectedUserFranchiseData.value;
  if (!userData) {
    validationMessage.value = t('Selecione um colaborador');
    validationMessageType.value = 'error';
    return;
  }

  isLoading.value = true;

  try {
    // Convert Date objects to YYYY-MM-DD format for API
    // Backend will set times: start=00:00:00, end=23:59:59 (or now if today)
    const startDateApiStr = fromDateToApiDate(formData.value?.startDate);
    const endDateApiStr = fromDateToApiDate(formData.value?.endDate);

    log('CreatePeriodModal', 'handleFinish', 'Sending dates', { startDateApiStr, endDateApiStr });

    const period = await createPeriodForEmployee(
      userData.userId,
      userData.franchiseId,
      startDateApiStr,
      endDateApiStr
    ) as { id: Id; [key: string]: unknown };

    log('CreatePeriodModal', 'handleFinish', 'Period created successfully:', period);

    // Emit event to refresh tables
    eventBus.emit('employeePayroll.periodCreated', {
      userId: userData.userId,
      franchiseId: userData.franchiseId,
      periodId: period.id
    });

    // Close this wizard
    handleClose();

    // Open PayrollModal for conciliation wizard
    // Pass only IDs - PayrollModal will fetch fresh data from API (single source of truth)
    setTimeout(() => {
      eventBus.emit('modal.payroll.open', {
        periodId: period.id,
        franchiseId: userData.franchiseId
      });
    }, 300);

  } catch (err) {
    const error = err as Error;
    console.error('[CreatePeriodModal] Failed to create period:', error);
    validationMessage.value = error.message || t('Erro ao criar período');
    validationMessageType.value = 'error';
    isLoading.value = false;
  }
};

/**
 * Handle modal close
 */
const handleClose = (): void => {
  log('CreatePeriodModal', 'handleClose', 'Closing modal');
  isVisible.value = false;
  resetForm();
};

/**
 * Load user+franchise combinations from selected franchises in filter service
 * Only includes combinations that have at least one active payment rule
 * Also fetches last payroll period dates for display
 */
const loadUserFranchiseCombinations = async (): Promise<void> => {
  isLoadingUsers.value = true;

  try {
    // Fetch payment rules and selected franchises in parallel
    const [paymentRulesResultRaw, selectedFranchises] = await Promise.all([
      listRules(),
      getSelectedFranchisesSelectArray()
    ]);
    const paymentRulesResult = paymentRulesResultRaw as unknown as PaymentRule[] | null;
    const franchiseList = selectedFranchises as Array<{ ID: Id; Name: string }>;

    log('CreatePeriodModal', 'loadUserFranchiseCombinations', 'Selected franchises:', franchiseList.length);
    log('CreatePeriodModal', 'loadUserFranchiseCombinations', 'Payment rules:', paymentRulesResult?.length ?? 0);

    // Build a Set of user+franchise combinations that have active payment rules
    // Rule is active if active === 1 || active === '1' || active === true
    const activeRulesCombinations = new Set<string>();
    if (paymentRulesResult && paymentRulesResult.length > 0) {
      for (const rule of paymentRulesResult) {
        const isActive = rule.active === 1 || rule.active === '1' || rule.active === true;
        if (isActive && rule.user_id && rule.franchise_id) {
          const key = `${rule.user_id}|${rule.franchise_id}`;
          activeRulesCombinations.add(key);
        }
      }
    }

    log('CreatePeriodModal', 'loadUserFranchiseCombinations', 'Active rule combinations:', activeRulesCombinations.size);

    // Build user+franchise combinations, filtering by active payment rules
    const combinationsForApi: Array<{ userId: Id; franchiseId: Id }> = [];
    const combinationMap = new Map<string, UserFranchiseData>();

    for (const franchise of franchiseList) {
      const franchiseId = franchise.ID;
      const franchiseName = franchise.Name;

      // Get employees for this franchise
      const franchiseEmployees = permissionsService.getFuncionariosSelectArrayByFranquiaID(Number(franchiseId));

      for (const emp of franchiseEmployees) {
        // Create unique key for this combination
        const key = `${emp.ID}|${franchiseId}`;

        // Only include if this user+franchise has at least one active payment rule
        if (!activeRulesCombinations.has(key)) {
          continue;
        }

        // Build list for API call
        combinationsForApi.push({
          userId: emp.ID,
          franchiseId: franchiseId
        });

        // Store full data in map for later retrieval
        combinationMap.set(key, {
          userId: emp.ID,
          userName: emp.Name,
          franchiseId: franchiseId,
          franchiseName: franchiseName
        });
      }
    }

    // Fetch last period dates for all combinations in bulk
    let lastPeriodsMap = new Map<string, Date | string>();
    if (combinationsForApi.length > 0) {
      lastPeriodsMap = await getLastPeriodsBulk(combinationsForApi) as Map<string, Date | string>;
      log('CreatePeriodModal', 'loadUserFranchiseCombinations', 'Fetched last periods for', lastPeriodsMap.size, 'combinations');
    }

    // Build final combinations list with display names including last period date
    const combinations: UserFranchiseListItem[] = [];
    for (const [key, data] of combinationMap) {
      const lastPeriodDate = lastPeriodsMap.get(key);

      // Format: "User Name - Franchise Name (Último: DD/MM/YYYY)" or "(Sem período anterior)"
      let displayName = `${data.userName} - ${data.franchiseName}`;
      if (lastPeriodDate) {
        const formattedDate = formatDate(lastPeriodDate as Date);
        displayName += ` (${t('Último')}: ${formattedDate})`;
      } else {
        displayName += ` (${t('Sem período anterior')})`;
      }

      combinations.push({
        ID: key,
        Name: displayName
      });
    }

    // Sort by display name for better UX
    combinations.sort((a, b) => a.Name.localeCompare(b.Name));

    userFranchiseList.value = combinations;
    userFranchiseMap.value = combinationMap;

    log('CreatePeriodModal', 'loadUserFranchiseCombinations', 'Loaded', combinations.length, 'user+franchise combinations with active rules');

  } catch (err) {
    console.error('[CreatePeriodModal] Failed to load user+franchise combinations:', err);
    userFranchiseList.value = [];
    userFranchiseMap.value = new Map();
  } finally {
    isLoadingUsers.value = false;
  }
};

/**
 * Fetch last period for auto-calculating start date
 * The start date will be the day after the last period's end date
 */
const fetchLastPeriod = async (): Promise<void> => {
  const userData = selectedUserFranchiseData.value;
  if (!userData) {
    return;
  }

  isLoadingPeriodData.value = true;
  errors.value = {};
  validationMessage.value = '';

  try {
    const lastPeriodEnd = await getLastPeriodEnd(userData.userId, userData.franchiseId) as Date | null;

    if (lastPeriodEnd) {
      // Has previous period - auto-calculate start as day after last end
      isFirstPeriod.value = false;
      // lastPeriodEnd is a Date object from the composable
      // We set start date as the day after the last period ended
      const nextDay = new Date(lastPeriodEnd);
      nextDay.setDate(nextDay.getDate() + 1);
      // Set to noon to avoid timezone issues (following typed data layer pattern)
      nextDay.setHours(12, 0, 0, 0);
      formData.value.startDate = nextDay;
      log('CreatePeriodModal', 'fetchLastPeriod', 'Auto-calculated start from last period:', formData.value.startDate);
    } else {
      // First period - user must set start manually
      isFirstPeriod.value = true;
      formData.value.startDate = null;
      log('CreatePeriodModal', 'fetchLastPeriod', 'First period - user must set start date');
    }
  } catch (err) {
    console.error('[CreatePeriodModal] Failed to fetch last period:', err);
    // Assume first period on error
    isFirstPeriod.value = true;
    formData.value.startDate = null;
  } finally {
    isLoadingPeriodData.value = false;
  }
};

/**
 * Validate form
 */
const validateForm = (): boolean => {
  errors.value = {};

  if (!selectedUserFranchiseData.value) {
    errors.value.userFranchise = t('Selecione um colaborador');
  }

  if (!formData.value?.startDate) {
    errors.value.startDate = t('Data início é obrigatória');
  }

  if (!formData.value?.endDate) {
    errors.value.endDate = t('Data fim é obrigatória');
  }

  validateDates();

  return Object.keys(errors.value).length === 0;
};

/**
 * Validate dates
 */
const validateDates = (): void => {
  const start = formData.value.startDate ? new Date(formData.value.startDate) : null;
  const end = formData.value.endDate ? new Date(formData.value.endDate) : null;

  if (!start || !end) {
    return;
  }

  // Normalize to start of day for comparison (date-only comparison)
  const startDay = new Date(start.getFullYear(), start.getMonth(), start.getDate());
  const endDay = new Date(end.getFullYear(), end.getMonth(), end.getDate());

  // Check end is after or equal to start (same day is allowed since times will differ)
  if (endDay < startDay) {
    errors.value.endDate = t('A data final deve ser igual ou posterior à data inicial');
    validationMessage.value = t('Selecione uma data de fim válida.');
    validationMessageType.value = 'error';
    return;
  }

  // Clear errors if valid
  delete errors.value.endDate;
  delete errors.value.startDate;

  // Calculate days (inclusive)
  const diffMs = endDay.getTime() - startDay.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24)) + 1; // +1 because inclusive
  validationMessage.value = `${t('Período de')} ${diffDays} ${t('dias')}`;
  validationMessageType.value = 'success';
};

/**
 * Reset form
 * Clears all state when modal is opened to prevent inheriting state from previous session
 */
const resetForm = (): void => {
  currentStep.value = 0;
  selectedUserFranchiseKey.value = null;
  formData.value = {
    startDate: null,
    endDate: null
  };
  errors.value = {};
  validationMessage.value = '';
  validationMessageType.value = 'info';
  isLoading.value = false;
  isLoadingPeriodData.value = false;
  isFirstPeriod.value = true;

  // Clear user+franchise list to prevent state inheritance
  // This ensures fresh data is loaded on each modal open
  userFranchiseList.value = [];
  userFranchiseMap.value = new Map();
};

/**
 * Handle modal open
 */
const handleOpen: EventCallback = async (): Promise<void> => {
  log('CreatePeriodModal', 'handleOpen', 'Opening modal');

  resetForm();
  isVisible.value = true;

  // Load user+franchise combinations
  await loadUserFranchiseCombinations();
};

// =============================================================================
// WATCHERS
// =============================================================================

// Watch for date changes to validate
watch(() => formData.value?.endDate, (newEnd: Date | null) => {
  if (newEnd && formData.value?.startDate) {
    validateDates();
  }
});

watch(() => formData.value?.startDate, (newStart: Date | null) => {
  if (newStart && formData.value?.endDate) {
    validateDates();
  }
});

// =============================================================================
// LIFECYCLE
// =============================================================================

onMounted(() => {
  log('CreatePeriodModal', 'mounted', 'Component mounted');
  eventBus.on('modal.createPeriod.open', handleOpen);
});

onBeforeUnmount(() => {
  log('CreatePeriodModal', 'onBeforeUnmount', 'Component unmounting');
  eventBus.off('modal.createPeriod.open', handleOpen);
});
</script>

<style scoped>
.step-content {
  padding: 1rem 0;
}

.step-content h3 {
  margin: 0 0 0.5rem 0;
  font-size: 1.25rem;
  font-weight: 600;
  color: #374151;
}

.step-description {
  margin: 0 0 1.5rem 0;
  color: #6B7280;
  font-size: 0.9375rem;
}

/* Helper Banner - matches Payment Rules Wizard */
.helper-banner {
  display: flex;
  gap: 1rem;
  padding: 1rem;
  background: var(--blue-50);
  border-left: 4px solid var(--blue-500);
  border-radius: 6px;
  margin-bottom: 1.5rem;
}

.helper-banner i {
  color: var(--blue-500);
  font-size: 1.5rem;
  flex-shrink: 0;
}

.helper-content strong {
  display: block;
  margin-bottom: 0.5rem;
  color: var(--blue-700);
}

.helper-content p {
  margin: 0 0 0.5rem 0;
  color: var(--blue-900);
  font-size: 0.9rem;
}

.helper-content ul {
  margin: 0;
  padding-left: 1.25rem;
}

.helper-content li {
  margin-bottom: 0.25rem;
  color: var(--blue-900);
  font-size: 0.9rem;
}

/* Warning Banner - matches Payment Rules Wizard */
.warning-banner {
  display: flex;
  gap: 1rem;
  padding: 1rem;
  background: var(--orange-50);
  border-left: 4px solid var(--orange-500);
  border-radius: 6px;
  margin-bottom: 1.5rem;
}

.warning-banner i {
  color: var(--orange-500);
  font-size: 1.5rem;
  flex-shrink: 0;
}

.warning-content strong {
  display: block;
  margin-bottom: 0.5rem;
  color: var(--orange-700);
}

.warning-content ul {
  margin: 0.5rem 0 0 0;
  padding-left: 1.25rem;
}

.warning-content li {
  margin-bottom: 0.25rem;
  color: var(--orange-900);
  font-size: 0.9rem;
}

.warning-content p {
  margin: 0.5rem 0 0 0;
  color: var(--orange-900);
  font-size: 0.9rem;
  line-height: 1.5;
}

.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  padding: 2rem;
  color: #6B7280;
}

.empty-message {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 1rem;
  background-color: #F3F4F6;
  border-radius: 8px;
  color: #6B7280;
}

.empty-message i {
  font-size: 1.25rem;
}

.selected-user-summary {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  padding: 0.75rem 1rem;
  background-color: #EBF5FF;
  border-left: 4px solid #3B82F6;
  border-radius: 4px;
  margin-bottom: 1.5rem;
  color: #1E40AF;
}

.selected-user-summary .summary-row {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.selected-user-summary i {
  font-size: 1rem;
  width: 1.25rem;
}

.selected-user-summary strong {
  font-weight: 600;
}

.field {
  margin-bottom: 1.5rem;
}

.field label {
  display: block;
  margin-bottom: 0.5rem;
  font-weight: 600;
  color: #374151;
}

.auto-label {
  font-weight: 400;
  color: #6B7280;
  font-size: 0.875rem;
}

.required-label {
  color: #EF4444;
}

.info-text {
  display: block;
  margin-top: 0.25rem;
  color: #6B7280;
  font-size: 0.8125rem;
}

.time-info {
  display: block;
  margin-top: 0.25rem;
  color: #9CA3AF;
  font-size: 0.75rem;
  font-style: italic;
}

/* Rule Summary Card - matches Payment Rules Wizard */
.rule-summary {
  padding: 1.25rem;
  background: var(--surface-0);
  border: 1px solid var(--surface-border);
  border-radius: 8px;
  margin-top: 1rem;
}

.summary-header {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 1rem;
  padding-bottom: 0.75rem;
  border-bottom: 1px solid var(--surface-border);
}

.summary-header i {
  font-size: 1.25rem;
  color: var(--primary-color);
}

.summary-header h3 {
  margin: 0;
  font-size: 1rem;
  font-weight: 600;
  color: var(--text-color);
}

.summary-content {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.summary-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 0.95rem;
}

.summary-label {
  color: var(--text-color-secondary);
  font-weight: 500;
}

.summary-value {
  color: var(--text-color);
  font-weight: 600;
  text-align: right;
}

.summary-value.amount {
  font-size: 1.125rem;
  color: var(--primary-color);
}

.summary-value small {
  font-size: 0.8em;
  color: var(--text-color-secondary);
  font-weight: 400;
}

/* Suggestion Container */
.suggestion-container {
  margin-top: 1rem;
  text-align: left;
}

/* Step Review Styles - matches Payment Rules Wizard */
.step-review {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.step-review .step-header h2 {
  margin: 0 0 0.5rem 0;
  font-size: 1.5rem;
  font-weight: 600;
  color: var(--text-color);
}

.step-review .step-description {
  margin: 0;
  color: var(--text-color-secondary);
  font-size: 1rem;
}

/* Important Notes - matches Payment Rules Wizard */
.important-notes {
  padding: 1.25rem;
  background: var(--surface-0);
  border: 1px solid var(--surface-border);
  border-radius: 8px;
}

.notes-header {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 1rem;
  padding-bottom: 0.75rem;
  border-bottom: 1px solid var(--surface-border);
}

.notes-header i {
  font-size: 1.25rem;
  color: var(--primary-color);
}

.notes-header h3 {
  margin: 0;
  font-size: 1rem;
  font-weight: 600;
  color: var(--text-color);
}

.notes-list {
  margin: 0;
  padding-left: 0;
  list-style: none;
}

.notes-list li {
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  margin-bottom: 0.75rem;
  font-size: 0.9rem;
  color: var(--text-color);
  line-height: 1.5;
}

.notes-list li i {
  color: var(--primary-color);
  flex-shrink: 0;
  margin-top: 0.1rem;
}

.loading-indicator {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1rem;
  background-color: #F3F4F6;
  border-radius: 4px;
  color: #6B7280;
  margin-top: 1rem;
}
</style>
